import React from 'react';
import { Dropdown, Form, Button } from 'react-bootstrap';
import { useIsMobile } from '../hooks/useMediaQuery';

/** Plans in the order customers move through them; anything else sorts after, and no plan last. */
const PLAN_ORDER = ['FREE_TRIAL', 'STANDARD', 'PROFESSIONAL', 'PREMIUM', 'ENTERPRISE'];
const NO_PLAN = 'NONE';

/** A company's plan, with companies that have none grouped under one key. */
export const planKey = (company) => company.accountType || NO_PLAN;

/** "FREE_TRIAL" reads as "Free trial". */
export const planLabel = (key) => (key === NO_PLAN || !key
  ? 'No plan'
  : key.charAt(0) + key.slice(1).toLowerCase().replace(/_/g, ' '));

export const planRank = (key) => (key === NO_PLAN ? 99 : PLAN_ORDER.includes(key) ? PLAN_ORDER.indexOf(key) : 50);

/** Every plan among the companies with how many are on it, in plan order. */
export const planOptions = (companies) => {
  const counts = new Map();
  companies.forEach((c) => counts.set(planKey(c), (counts.get(planKey(c)) || 0) + 1));
  return [...counts.entries()]
    .map(([key, total]) => ({ key, total }))
    .sort((a, b) => planRank(a.key) - planRank(b.key) || a.key.localeCompare(b.key));
};

/**
 * A dropdown of plan tickboxes: untick a plan to hide its companies.
 *
 * @param {string} id - the toggle's id, unique on the page.
 * @param {Array} options - from {@link planOptions}.
 * @param {Set} hidden - plan keys currently hidden.
 * @param {Function} onChange - called with the new Set of hidden plan keys.
 */
const PlanFilter = ({ id, options, hidden, onChange }) => {
  const isMobile = useIsMobile();
  const toggle = (key) => {
    const next = new Set(hidden);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  };
  const shown = options.filter((o) => !hidden.has(o.key)).length;
  return (
    <Dropdown autoClose="outside">
      {/* On phones it fills its cell in a filter row, like the selects beside it */}
      <Dropdown.Toggle size="sm" variant="outline-secondary" id={id} className={isMobile ? 'w-100' : undefined}>
        {hidden.size === 0 ? 'All plans' : `${shown} of ${options.length} plans`}
      </Dropdown.Toggle>
      <Dropdown.Menu style={{ minWidth: 200 }}>
        {options.map(({ key, total }) => (
          <div key={key} className="px-3 py-1" style={{ whiteSpace: 'nowrap' }}>
            <Form.Check
              type="checkbox"
              id={`${id}-${key}`}
              checked={!hidden.has(key)}
              onChange={() => toggle(key)}
              label={`${planLabel(key)} (${total.toLocaleString('en-GB')})`}
            />
          </div>
        ))}
        {hidden.size > 0 && (
          <>
            <Dropdown.Divider />
            <div className="px-3">
              <Button variant="link" size="sm" className="p-0" onClick={() => onChange(new Set())}>Show all plans</Button>
            </div>
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default PlanFilter;
